class AddToCartBulk extends HTMLElement {

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.cartQuantity = Number(this.dataset.cartQuantity || 0);

    this._i18n = {
      available:  this.dataset.i18nAvailable  || 'Verfügbar',
      unit:       this.dataset.i18nUnit       || 'Stk',
      inCart:     this.dataset.i18nInCart     || 'Im Warenkorb',
      inCartQty:  this.dataset.i18nInCartQty  || 'im Warenkorb',
    };

    this.variants = JSON.parse(this.dataset.variants || '[]');

    // W3: cache mixed-prices result — variants never change after parse
    this._mixedPrices = this._hasMixedPrices();

    this.tiers = JSON.parse(this.dataset.discountTiers || '[]')
      .map(t => ({ min: Number(t.min), percent: Number(t.percent) }))
      .sort((a, b) => a.min - b.min);

    this.rows = Array.from(this.querySelectorAll('.bulk-variant'));

    // W1: cache input references once instead of querying on every call
    this._rowInputs = this.rows.map(row => row.querySelector('input'));

    this._cartInfoContainer = this.querySelector('.bulk-cart-info');
    this._cartInfoSpan      = this.querySelector('.bulk-cart-current');

    this.bar        = this.querySelector('.bulk-tier-bar');
    this.tierPoints = Array.from(this.querySelectorAll('.bulk-tier-point'));
    this.resetBtn   = this.querySelector('.bulk-reset');

    this.summaryQty   = this.querySelector('.bulk-summary-qty');
    this.summaryUnit  = this.querySelector('.bulk-summary-unit');
    this.summarySave  = this.querySelector('.bulk-summary-save');
    this.summaryFinal = this.querySelector('.bulk-summary-final');

    this._applyInventoryConstraints();
    this._initQtyControls();
    this._bindReset();
    this._setupCartSync();
    this._updateCartInfo();
    this._recalculate();
  }

  /* ---------------- Utils ---------------- */

  _hasMixedPrices() {
    const prices = this.variants.map(v => v.price);
    return prices.some(p => p !== prices[0]);
  }

  _applyDiscount(price, percent) {
    if (!percent) return price;
    const disc = Math.floor(price * percent / 100);
    return price - disc;
  }

  _formatMoney(cents) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency', currency: 'EUR'
    }).format(cents / 100);
  }

  /* ---------------- Inventory constraints ---------------- */

  _applyInventoryConstraints() {
    this.rows.forEach((row, i) => {
      const variant             = this.variants[i];
      const inventoryManagement = row.dataset.inventoryManagement
        ?? variant?.inventory_management
        ?? null;
      const stock = Number(row.dataset.inventory
        ?? variant?.inventory_quantity
        ?? 0);

      const input    = row.querySelector('input');
      const btnPlus  = row.querySelector('.qty-plus');
      const btnMinus = row.querySelector('.qty-minus');
      const stockEl  = row.querySelector('.bulk-stock');

      const isTracked = !!inventoryManagement;
      if (!isTracked) return;

      // W5: null guards before any use of input/btnPlus/btnMinus
      if (!input || !btnPlus || !btnMinus) return;

      if (stockEl) {
        if (stock > 0) {
          if (stock <= 5) {
            stockEl.innerHTML = `${this._i18n.available}: <span class="bulk-stock--low">${stock} ${this._i18n.unit}</span>`;
          } else {
            stockEl.textContent = `${this._i18n.available}: ${stock} ${this._i18n.unit}`;
          }
        }
      }

      if (stock <= 0) {
        input.value       = 0;
        input.disabled    = true;
        btnPlus.disabled  = true;
        btnMinus.disabled = true;
        row.classList.add('bulk-variant--unavailable');
        return;
      }

      input.max = stock;
      row.dataset.maxStock = stock;
    });
  }

  _clampToStock(row, input) {
    const max = Number(row.dataset.maxStock);
    if (!max) return;
    if (Number(input.value) > max) {
      input.value = max;
    }
  }

  /* ---------------- Cart sync ---------------- */

  _setupCartSync() {
    // O7: require both subscribe and publish to be present
    if (typeof window.subscribe === 'function' && typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      this._cartUpdateUnsub = subscribe(
        window.PUB_SUB_EVENTS.cartUpdate,
        () => this._syncCartQuantity()
      );
    }

    this._cartObserver = new MutationObserver(() => this._syncCartQuantity());

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      this._cartObserver.observe(drawer, { childList: true, subtree: true });
    }
  }

  // W7: prevent concurrent fetches with a _syncPending guard
  async _syncCartQuantity() {
    if (this._syncPending) return;
    this._syncPending = true;
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(r => r.json());

      const newCartQty = cart.items
        .filter(item => item.product_id == this.productId)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (newCartQty !== this.cartQuantity) {
        this.cartQuantity = newCartQty;
        this._updateCartInfo();
        this._recalculate();
      }
    } catch (e) {
      console.warn('[add-to-cart-bulk] cart sync failed', e);
    } finally {
      this._syncPending = false;
    }
  }

  _updateCartInfo() {
    if (!this._cartInfoContainer || !this._cartInfoSpan) return;
    if (this.cartQuantity > 0) {
      this._cartInfoSpan.innerHTML = `${this._i18n.inCart}: <strong>${this.cartQuantity} ${this._i18n.unit}</strong>`;
      this._cartInfoContainer.style.display = 'block';
    } else {
      this._cartInfoContainer.style.display = 'none';
    }
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
    if (this._cartObserver) this._cartObserver.disconnect();
  }

  /* ---------------- Controls ---------------- */

  reset() {
    // W1: use cached input references
    this.rows.forEach((row, i) => {
      if (!row.classList.contains('bulk-variant--unavailable')) {
        this._rowInputs[i].value = 0;
      }
    });
    this._recalculate();
  }

  _initQtyControls() {
    this.rows.forEach((row, i) => {
      if (row.classList.contains('bulk-variant--unavailable')) return;

      // W1: use cached input reference
      const input = this._rowInputs[i];
      const minus = row.querySelector('.qty-minus');
      const plus  = row.querySelector('.qty-plus');

      minus.addEventListener('click', () => {
        input.value = Math.max(0, Number(input.value) - 1);
        this._recalculate();
      });

      plus.addEventListener('click', () => {
        const max = Number(row.dataset.maxStock) || Infinity;
        input.value = Math.min(Number(input.value) + 1, max);
        this._recalculate();
      });

      // W6: debounce recalculate via requestAnimationFrame
      input.addEventListener('input', () => {
        input.value = Math.max(0, Number(input.value) || 0);
        this._clampToStock(row, input);
        cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => this._recalculate());
      });
    });
  }

  _bindReset() {
    if (!this.resetBtn) return;
    this.resetBtn.addEventListener('click', () => this.reset());
  }

  /* ---------------- Payload ---------------- */

  // W2: accept pre-computed currentTier (and bulkQty) from _recalculate;
  //     fall back to computing internally when called externally with no args.
  getBulkPayload(currentTier, bulkQty) {
    if (currentTier === undefined) {
      const bq       = this._getBulkQuantity();
      const totalQty = this.cartQuantity + bq;
      const { currentTier: ct } = this._getTierInfo(totalQty);
      currentTier = ct;
    }

    const items = [];

    this.rows.forEach((row, i) => {
      // W9: bounds check
      const variant = this.variants[i];
      if (!variant) return;

      // W1 + W4: cached input reference with optional chaining
      const qty = Number(this._rowInputs[i]?.value || 0);
      if (qty > 0) {
        const basePrice  = variant.price;
        const finalPrice = currentTier
          ? this._applyDiscount(basePrice, currentTier.percent)
          : basePrice;

        items.push({
          variant_id:    variant.variant_id,
          quantity:      qty,
          color:         variant.color,
          size:          variant.size,
          price:         finalPrice,
          product_title: variant.product_title
        });
      }
    });

    return items;
  }

  /* ---------------- Main calc ---------------- */

  _recalculate() {
    const bulkQty  = this._getBulkQuantity();
    const totalQty = this.cartQuantity + bulkQty;
    const tierInfo = this._getTierInfo(totalQty);

    this._updateProgress(bulkQty);
    this._updatePrices(tierInfo.currentTier);
    this._updateSummary(bulkQty, tierInfo.currentTier);

    // W2: pass pre-computed values — no redundant recompute inside getBulkPayload
    this.dispatchEvent(new CustomEvent('bulk-payload-changed', {
      detail: { items: this.getBulkPayload(tierInfo.currentTier, bulkQty) },
      bubbles: true
    }));
  }

  _getBulkQuantity() {
    // W1: iterate over cached input references
    return this._rowInputs.reduce((sum, input) => {
      return sum + Number(input?.value || 0);
    }, 0);
  }

  _getTierInfo(totalQty) {
    let currentTier = null;
    for (let i = this.tiers.length - 1; i >= 0; i--) {
      if (totalQty >= this.tiers[i].min) {
        currentTier = this.tiers[i];
        break;
      }
    }
    return { currentTier };
  }

  /* ---------------- Progress ---------------- */

  _updateProgress(bulkQty) {
    const totalQty = this.cartQuantity + bulkQty;

    // Milestones are evenly spaced (tier i of n at i/n * 100%),
    // so the bar fills piecewise: linearly between neighbouring tiers.
    if (this.bar && this.tiers.length > 0) {
      const step = 100 / this.tiers.length;
      let progress = 100;
      let prevMin  = 0;
      for (let i = 0; i < this.tiers.length; i++) {
        const min = this.tiers[i].min;
        if (totalQty < min) {
          progress = step * i + step * (totalQty - prevMin) / (min - prevMin);
          break;
        }
        prevMin = min;
      }
      this.bar.style.width = `${progress}%`;
    }

    let currentMarked = false;
    this.tierPoints.forEach(point => {
      const min = Number(point.dataset.min);
      point.classList.remove('is-reached', 'is-current');
      if (totalQty >= min) {
        point.classList.add('is-reached');
        return;
      }
      if (!currentMarked) {
        point.classList.add('is-current');
        currentMarked = true;
      }
    });
  }

  /* ---------------- Prices ---------------- */

  _updatePrices(currentTier) {
    // W3: use cached _mixedPrices
    const mixed = this._mixedPrices;

    this.rows.forEach((row, i) => {
      // W9: bounds check
      const variant = this.variants[i];
      if (!variant) return;

      const priceEl   = row.querySelector('.bulk-price');
      const basePrice = variant.price;
      const final     = currentTier
        ? this._applyDiscount(basePrice, currentTier.percent)
        : basePrice;

      priceEl.textContent = mixed ? ` (${this._formatMoney(final)})` : '';
    });
  }

  /* ---------------- Summary ---------------- */

  _updateSummary(bulkQty, currentTier) {
    if (this.summaryUnit) {
      const bestTier = this.tiers.length > 0 ? this.tiers[this.tiers.length - 1] : null;

      if (this._mixedPrices) {
        if (bulkQty === 0 && bestTier) {
          const bestPrices = this.variants.map(v => this._applyDiscount(v.price, bestTier.percent));
          this.summaryUnit.textContent = `ab ${this._formatMoney(Math.min(...bestPrices))}`;
        } else {
          const prices = this.variants.map(v =>
            currentTier ? this._applyDiscount(v.price, currentTier.percent) : v.price
          );
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          this.summaryUnit.textContent = min === max
            ? this._formatMoney(min)
            : `${this._formatMoney(min)} – ${this._formatMoney(max)}`;
        }
      } else {
        const basePrice = this.variants[0]?.price ?? 0;
        if (bulkQty === 0 && bestTier) {
          this.summaryUnit.textContent = `ab ${this._formatMoney(this._applyDiscount(basePrice, bestTier.percent))}`;
        } else {
          const unitPrice = currentTier ? this._applyDiscount(basePrice, currentTier.percent) : basePrice;
          this.summaryUnit.textContent = this._formatMoney(unitPrice);
        }
      }
    }

    let baseTotal  = 0;
    let finalTotal = 0;

    this.rows.forEach((row, i) => {
      // W9: bounds check
      const variant = this.variants[i];
      if (!variant) return;

      // W1 + W4: cached input reference with optional chaining
      const qty   = Number(this._rowInputs[i]?.value || 0);
      const price = variant.price;
      baseTotal  += qty * price;
      finalTotal += qty * (currentTier
        ? this._applyDiscount(price, currentTier.percent)
        : price);
    });

    const saved = baseTotal - finalTotal;

    if (this.summaryQty) {
      const cartText = this.cartQuantity > 0
        ? ` <span class="bulk-summary-qty__cart">(+${this.cartQuantity} ${this._i18n.inCartQty})</span>` : '';
      this.summaryQty.innerHTML = `${bulkQty} ${this._i18n.unit}${cartText}`;
    }
    if (this.summarySave)  this.summarySave.textContent  = this._formatMoney(saved);
    if (this.summaryFinal) this.summaryFinal.textContent = this._formatMoney(finalTotal);
  }
}

// W8: guard against re-registration (e.g. when the script is loaded more than once)
if (!customElements.get('add-to-cart-bulk')) {
  customElements.define('add-to-cart-bulk', AddToCartBulk);
}
